/**
 * WerkstattBetriebView – Content-View für die Werkstatt-Job-Verwaltungsseite
 * (NT-WERKSTATT-BETRIEB-02B). Kein Modal — rendert direkt in den Jobs-Content-Bereich
 * (`dashboard.ts::renderJobsTab()`), App-Shell (Header/Nav/Sidebar) bleibt stehen. Muster 1:1
 * von `PfandBetriebModal.ts` übernommen: `prepareWerkstattView()` synchron vor dem Öffnen,
 * `renderWerkstattViewShell()` als Markup innerhalb der Jobs-Section, `mountWerkstattView()`
 * nach dem DOM-Insert (lädt Daten + Listener), `resetWerkstattViewState()` beim Verlassen/Unmount.
 *
 * Teile-Puffer (Real-Inventory-Redesign): kein separates EINLAGERN/AUSLAGERN-Formular mehr —
 * der Puffer IST das echte Lagerhaus-Inventar des einen dedizierten Lagers. Teile kommen
 * ausschließlich per normalem VERSENDEN (Lagerhaus) rein und raus, eingeschränkt auf
 * werkstatt-relevante Teile (Backend-L-GATE). Diese View zeigt nur noch den Bestand + die
 * Zuweisungs-Verwaltung (Lager zuweisen/lösen), keine eigene Transfer-Mechanik mehr.
 */

import { api, ApiError } from '../services/api.js';
import { formatItemKey } from '../utils/formatters.js';
import { formatPropertyDisplayName, renderPropertyOptions, type PropertySelectItem } from '../utils/property-select.js';
import { showToast } from '../utils/toast.js';
import { refreshBalance } from '../utils/refreshBalance.js';

const ROOT_ID = 'werkstatt-betrieb-root';

// Karenzfrist bis zur Kündigung eines unbezahlten Mechanikers (WERKSTATT_MECHANIC_KARENZ_STUNDEN,
// Backend werkstatt.constants.ts) — reiner Anzeige-Wert, kein Shared-Import (etabliertes Pattern).
const WERKSTATT_MECHANIC_KARENZ_STUNDEN = 12;

// Sentinel-Key aus werkstatt.constants.ts (Backend) — reines Anzeige-/Vergleichs-Duplikat,
// kein Shared-Import für reine Anzeige-Werte (etabliertes Pattern im Repo).
const WERKSTATT_BUFFER_DEDICATION_KEY = '__werkstatt_buffer__';

// Tier-Zuordnung der 13 Werkstatt-Teile (werkstatt.definitions.ts, NT-WERKSTATT-TEILE-01) —
// Backend liefert im Puffer-Bestand keine Tier-Info mit, daher hier dupliziert (reiner
// Anzeige-Wert, kein Shared-Import-Pattern).
const WERKSTATT_PART_TIER: Record<string, 'GENERISCH' | 'PREMIUM'> = {
  schrauben_muttern: 'GENERISCH',
  aluminiumblech: 'GENERISCH',
  autobatterie_alt: 'GENERISCH',
  hydraulikschlauch: 'GENERISCH',
  kabelbaum: 'GENERISCH',
  bremsbelaege: 'GENERISCH',
  auspufftopf: 'GENERISCH',
  scheinwerfer: 'GENERISCH',
  getriebe: 'PREMIUM',
  steuergeraet: 'PREMIUM',
  turbolader: 'PREMIUM',
  motorblock: 'PREMIUM',
  katalysator: 'PREMIUM',
};

// Reputations-Schwellen fürs Klientel-Band (WERKSTATT_STAMMKUNDEN_REP_THRESHOLD/
// WERKSTATT_PREMIUM_REP_THRESHOLD, Backend werkstatt.constants.ts) — reiner Anzeige-Wert für die
// AUFTRAGS-TIER-Zusammenfassung, kein Shared-Import (etabliertes Pattern).
const WERKSTATT_STAMMKUNDEN_REP_THRESHOLD = 15;
const WERKSTATT_PREMIUM_REP_THRESHOLD = 50;

// Tätigkeit je Leitteil (NT-WERKSTATT-FIX-01, Auftragskarten-Titel) — reiner Anzeige-Wert, kein
// Backend-Gegenstück nötig (keine Logik hängt am Titel-Text).
const WERKSTATT_LEITTEIL_TAETIGKEIT: Record<string, string> = {
  aluminiumblech: 'KAROSSERIE AUSBESSERN',
  autobatterie_alt: 'BATTERIE ERSETZEN',
  hydraulikschlauch: 'HYDRAULIKSCHLAUCH ERNEUERN',
  kabelbaum: 'KABELBAUM ERNEUERN',
  bremsbelaege: 'BREMSEN WARTEN',
  auspufftopf: 'AUSPUFF AUSTAUSCHEN',
  scheinwerfer: 'SCHEINWERFER TAUSCHEN',
  getriebe: 'GETRIEBE EINBAUEN',
  steuergeraet: 'STEUERGERÄT CODIEREN',
  turbolader: 'TURBOLADER EINBAUEN',
  motorblock: 'MOTOR ÜBERHOLEN',
  katalysator: 'KATALYSATOR ERSETZEN',
};

function werkstattTaetigkeit(leitTeilKey: string): string {
  return WERKSTATT_LEITTEIL_TAETIGKEIT[leitTeilKey] ?? formatItemKey(leitTeilKey).toUpperCase();
}

interface WerkstattMechanicView {
  id: string;
  name: string;
  hiredAt: string;
  status: 'BUSY' | 'CRAFTING' | 'PAUSED' | 'IDLE';
  currentCustomerId: string | null;
  unpaidSinceAt: string | null;
  craftRecipeId: string | null;
  craftCompletesAt: string | null;
  craftOutputItemKey: string | null;
}

interface WerkstattCustomerView {
  id: string;
  leitTeilKey: string;
  tier: 'GENERISCH' | 'PREMIUM';
  klientelBand: 'LAUFKUNDSCHAFT' | 'STAMMKUNDEN' | 'SZENE';
  verbrauchsmaterialQty: number;
  rewardCents: string;
  reputationGain: number;
  durationSeconds: number;
  status: 'WAITING' | 'IN_PROGRESS' | 'DONE' | 'EXPIRED';
  isPrioritized: boolean;
  createdAt: string;
  expiresAt: string;
  assignedAt: string | null;
  payoutAt: string | null;
}

interface WerkstattBetriebOverview {
  kasseCents: string;
  reputation: number;
  mechanics: WerkstattMechanicView[];
  maxMechanics: number;
  nextHireCostCents: string;
  lohnProStundeCents: string;
  reichweiteStunden: number | null;
  waitingCustomers: WerkstattCustomerView[];
  inProgressCustomers: WerkstattCustomerView[];
  bufferParts: { itemKey: string; quantity: number }[];
  bufferTotal: number;
  bufferCapacity: number;
}

interface WerkstattBetriebResponse {
  unlocked: boolean;
  overview: WerkstattBetriebOverview | null;
}

interface OwnedWarehouseLite {
  id: string;
  subType: string;
  customName: string | null;
  dedicatedItemKey: string | null;
}

interface PropertiesResponseLite {
  warehouses: OwnedWarehouseLite[];
}

let overview: WerkstattBetriebOverview | null = null;
let warehouses: OwnedWarehouseLite[] = [];
let loading = false;
let busy = false;
let depositAmount = '';
let withdrawAmount = '';
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const NOX = '&#8358;';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtNox(cents: string | number): string {
  return `${(Number(cents) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}&nbsp;${NOX}`;
}

function fmtRemaining(targetIso: string): string {
  const ms = new Date(targetIso).getTime() - Date.now();
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${String(h)}:${pad(m)}:${pad(s)}` : `${String(m)}:${pad(s)}`;
}

/** Vor dem Wechsel in die View aufrufen (synchron, vor renderAll()). */
export function prepareWerkstattView(): void {
  overview = null;
  warehouses = [];
  busy = false;
  depositAmount = '';
  withdrawAmount = '';
  loading = true;
}

/** Beim Verlassen der View (Zurück-Button) bzw. beim Unmount des gesamten Dashboards. */
export function resetWerkstattViewState(): void {
  overview = null;
  warehouses = [];
  loading = false;
  busy = false;
  if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }
}

/** Synchrones Markup für den Jobs-Content-Bereich — Daten kommen erst danach via mount(). */
export function renderWerkstattViewShell(): string {
  return `<div id="${ROOT_ID}">${renderInner()}</div>`;
}

/** Nach dem DOM-Insert aufrufen: lädt die Daten und hängt die Listener an den Root-Container. */
export async function mountWerkstattView(): Promise<void> {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  await reload();
  loading = false;
  render();
  attachListeners(root);

  if (countdownTimer !== null) clearInterval(countdownTimer);
  countdownTimer = setInterval(updateCountdownsInPlace, 1000);
}

async function reload(): Promise<void> {
  try {
    const [betriebRes, propsRes] = await Promise.all([
      api.get<WerkstattBetriebResponse>('/werkstatt/betrieb'),
      api.get<PropertiesResponseLite>('/properties'),
    ]);
    overview = betriebRes.overview;
    warehouses = propsRes.warehouses;
  } catch {
    showToast('Werkstatt konnte nicht geladen werden.', 'error');
  }
}

/** Restzeiten clientseitig herunterzählen (Teil 3) — Wahrheit bleibt der Server, nur Textinhalt patchen. */
function updateCountdownsInPlace(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.querySelectorAll<HTMLElement>('[data-countdown-target]').forEach((el) => {
    const target = el.dataset.countdownTarget;
    if (!target) return;
    el.textContent = fmtRemaining(target);
  });
}

// ─── Sektion 1: Kopf ────────────────────────────────────────────────────────────

/**
 * Klientel-Bänder, die bei der aktuellen Reputation grundsätzlich erreichbar sind. Muster wie
 * getWerkstattKlientelBand() im Backend, hier nur die Zusammenfassung aller aktuell erreichbaren
 * Bänder statt eines einzelnen.
 */
function renderAuftragsTierLabel(reputation: number): string {
  const bands = ['LAUFKUNDSCHAFT'];
  if (reputation >= WERKSTATT_STAMMKUNDEN_REP_THRESHOLD) bands.push('STAMMKUNDEN');
  if (reputation >= WERKSTATT_PREMIUM_REP_THRESHOLD) bands.push('SZENE');
  return bands.join(' + ');
}

function renderKopf(o: WerkstattBetriebOverview): string {
  const kasseEuros = (Number(o.kasseCents) / 100).toFixed(2);
  const hasLohn = Number(o.lohnProStundeCents) > 0;
  return `
    <div class="pfand-betrieb-kasse-highlight">
      <span class="pfand-betrieb-kasse-highlight-label">KASSE</span>
      <span class="pfand-betrieb-kasse-highlight-value">${fmtNox(o.kasseCents)}</span>
    </div>
    <div class="pfand-betrieb-uebersicht">
      <div class="pfand-betrieb-uebersicht-row">
        <span>REFERENZEN</span>
        <span class="val-green">${String(o.reputation)}</span>
      </div>
      <div class="pfand-betrieb-uebersicht-row">
        <span>AUFTRAGS-TIER</span>
        <span>${renderAuftragsTierLabel(o.reputation)}</span>
      </div>
      <div class="pfand-betrieb-uebersicht-row">
        <span>LOHN/STD (alle Mechaniker)</span>
        <span class="val-red">−${fmtNox(o.lohnProStundeCents)}</span>
      </div>
      ${hasLohn && o.reichweiteStunden !== null ? `
        <div class="pfand-betrieb-uebersicht-row">
          <span>KASSE REICHT NOCH (ohne neue Aufträge)</span>
          <span class="val-orange">~${String(o.reichweiteStunden)}&nbsp;STD</span>
        </div>
      ` : ''}
    </div>
    <p class="dash-meta-hint">
      Jeder Mechaniker kostet Lohn — auch im Leerlauf. Reicht die Kasse nicht, pausieren
      ALLE Mechaniker gemeinsam; bleibt es länger als ${String(WERKSTATT_MECHANIC_KARENZ_STUNDEN)}h
      unbezahlt, wird der jeweilige Mechaniker gekündigt (kein Wiedereinstellen, keine Rückerstattung).
    </p>
    <div class="pfand-betrieb-kasse-actions">
      <div class="pfand-betrieb-kasse-row">
        <input type="number" min="1" class="pfand-betrieb-amount-input" id="werkstatt-deposit-input" placeholder="Betrag eingeben" value="${esc(depositAmount)}" />
        <div class="pfand-betrieb-kasse-actions-zone">
          <button class="pfand-betrieb-btn-gold" type="button" id="werkstatt-deposit-btn" ${busy ? 'disabled' : ''}>EINZAHLEN</button>
        </div>
      </div>
      <div class="pfand-betrieb-kasse-row">
        <input type="number" min="1" class="pfand-betrieb-amount-input" id="werkstatt-withdraw-input" placeholder="Betrag eingeben" value="${esc(withdrawAmount)}" />
        <div class="pfand-betrieb-kasse-actions-zone">
          <button class="pfand-betrieb-max-btn" type="button" id="werkstatt-withdraw-max-btn" data-max="${kasseEuros}" ${busy || o.kasseCents === '0' ? 'disabled' : ''}>MAX</button>
          <button class="pfand-betrieb-btn-gold" type="button" id="werkstatt-withdraw-btn" ${busy ? 'disabled' : ''}>ENTNEHMEN</button>
        </div>
      </div>
    </div>
  `;
}

// ─── Sektion 2: Mechaniker ──────────────────────────────────────────────────────

function renderMechanicRow(m: WerkstattMechanicView, inProgress: WerkstattCustomerView[]): string {
  let statusText: string;
  let statusClass: string;
  let countdown = '';

  if (m.status === 'BUSY') {
    const customer = inProgress.find((c) => c.id === m.currentCustomerId);
    statusText = 'REPARIERT';
    statusClass = 'werkstatt-mech-status--busy';
    if (customer?.payoutAt) {
      countdown = ` <span data-countdown-target="${esc(customer.payoutAt)}">${fmtRemaining(customer.payoutAt)}</span>`;
    }
  } else if (m.status === 'CRAFTING') {
    const outputLabel = m.craftOutputItemKey ? formatItemKey(m.craftOutputItemKey) : 'Teil';
    statusText = `CRAFTET: ${outputLabel.toUpperCase()}`;
    statusClass = 'werkstatt-mech-status--crafting';
    if (m.craftCompletesAt) {
      countdown = ` <span data-countdown-target="${esc(m.craftCompletesAt)}">${fmtRemaining(m.craftCompletesAt)}</span>`;
    }
  } else if (m.status === 'PAUSED') {
    statusText = 'PAUSIERT (UNBEZAHLT)';
    statusClass = 'werkstatt-mech-status--paused';
    if (m.unpaidSinceAt) {
      const fireAt = new Date(new Date(m.unpaidSinceAt).getTime() + WERKSTATT_MECHANIC_KARENZ_STUNDEN * 3_600_000).toISOString();
      countdown = ` — Kündigung in <span data-countdown-target="${esc(fireAt)}">${fmtRemaining(fireAt)}</span>`;
    }
  } else {
    statusText = 'BEREIT';
    statusClass = 'werkstatt-mech-status--idle';
  }

  return `
    <div class="pfand-betrieb-emp-row">
      <span class="pfand-betrieb-emp-name">${esc(m.name)}</span>
      <span class="werkstatt-mech-status ${statusClass}">${statusText}${countdown}</span>
    </div>
  `;
}

function renderMechanikerSektion(o: WerkstattBetriebOverview): string {
  const atMax = o.mechanics.length >= o.maxMechanics;
  return `
    <h3 class="pfand-betrieb-section-title">
      MECHANIKER (${String(o.mechanics.length)}/${String(o.maxMechanics)})
      <span class="werkstatt-header-actions">
        <button class="btn-pfand-abgeben" type="button" data-werkstatt-craft-open ${busy ? 'disabled' : ''}>
          TEILE CRAFTEN
        </button>
        <button class="btn-pfand-abgeben" type="button" id="werkstatt-hire-btn" ${busy || atMax ? 'disabled' : ''}>
          EINSTELLEN — ${fmtNox(o.nextHireCostCents)}
        </button>
      </span>
    </h3>
    ${atMax ? '<p class="dash-meta-hint">Werkstatt-Job-Level-Grenze erreicht — höheres Job-Level schaltet weitere Slots frei.</p>' : ''}
    <div class="pfand-betrieb-emp-list">
      ${o.mechanics.length === 0
        ? '<p class="dash-empty">Noch keine Mechaniker eingestellt.</p>'
        : o.mechanics.map((m) => renderMechanicRow(m, o.inProgressCustomers)).join('')}
    </div>
  `;
}

// ─── Sektion 3: Kunden-Queue ─────────────────────────────────────────────────────

function bufferQty(o: WerkstattBetriebOverview, itemKey: string): number {
  return o.bufferParts.find((p) => p.itemKey === itemKey)?.quantity ?? 0;
}

const WERKSTATT_KLIENTEL_BAND_CLASS: Record<WerkstattCustomerView['klientelBand'], string> = {
  LAUFKUNDSCHAFT: 'werkstatt-customer-band--laufkundschaft',
  STAMMKUNDEN: 'werkstatt-customer-band--stammkunden',
  SZENE: 'werkstatt-customer-band--szene',
};

function renderCustomerRow(c: WerkstattCustomerView, o: WerkstattBetriebOverview): string {
  const missingLeitTeil = bufferQty(o, c.leitTeilKey) < 1;
  const missingVerbrauch = bufferQty(o, 'schrauben_muttern') < c.verbrauchsmaterialQty;
  const missingParts = missingLeitTeil || missingVerbrauch;

  return `
    <div class="werkstatt-customer-row${missingParts ? ' werkstatt-customer-row--missing' : ''}">
      <div class="werkstatt-customer-header">
        <span class="werkstatt-customer-band ${WERKSTATT_KLIENTEL_BAND_CLASS[c.klientelBand]}">${c.klientelBand}</span>
        <span class="werkstatt-customer-title">${esc(werkstattTaetigkeit(c.leitTeilKey))}</span>
      </div>
      <div class="werkstatt-customer-bom">
        <span>1&times; ${esc(formatItemKey(c.leitTeilKey))}${missingLeitTeil ? ' <span class="werkstatt-missing-badge">FEHLT</span>' : ''}</span>
        <span>${String(c.verbrauchsmaterialQty)}&times; Schrauben &amp; Muttern${missingVerbrauch ? ' <span class="werkstatt-missing-badge">FEHLT</span>' : ''}</span>
      </div>
      <div class="werkstatt-customer-reward">
        <span class="val-green">${fmtNox(c.rewardCents)}</span>
        <span class="dash-meta-hint">+${String(c.reputationGain)} REFERENZEN</span>
      </div>
      <span class="werkstatt-customer-expiry">Verfällt in <span data-countdown-target="${esc(c.expiresAt)}">${fmtRemaining(c.expiresAt)}</span></span>
      <button class="btn-pfand-abgeben" type="button" data-werkstatt-prioritize="${esc(c.id)}" ${busy || c.isPrioritized ? 'disabled' : ''}>
        ${c.isPrioritized ? 'VORGEZOGEN' : 'VORZIEHEN'}
      </button>
    </div>
  `;
}

function renderKundenSektion(o: WerkstattBetriebOverview): string {
  return `
    <h3 class="pfand-betrieb-section-title">AUFTRÄGE (${String(o.waitingCustomers.length)})</h3>
    <div class="werkstatt-customer-list">
      ${o.waitingCustomers.length === 0
        ? '<p class="dash-empty">Keine wartenden Aufträge.</p>'
        : o.waitingCustomers.map((c) => renderCustomerRow(c, o)).join('')}
    </div>
  `;
}

// ─── Sektion 4: Teile-Puffer ──────────────────────────────────────────────────────

function renderPartRow(p: { itemKey: string; quantity: number }): string {
  return `
    <div class="werkstatt-part-row">
      <span>${esc(formatItemKey(p.itemKey))}</span>
      <span>${String(p.quantity)}</span>
    </div>
  `;
}

function renderPufferSektion(o: WerkstattBetriebOverview): string {
  const generisch = o.bufferParts.filter((p) => (WERKSTATT_PART_TIER[p.itemKey] ?? 'GENERISCH') === 'GENERISCH' || !(p.itemKey in WERKSTATT_PART_TIER));
  const premium = o.bufferParts.filter((p) => WERKSTATT_PART_TIER[p.itemKey] === 'PREMIUM');
  const dedicated = warehouses.find((w) => w.dedicatedItemKey === WERKSTATT_BUFFER_DEDICATION_KEY);
  const undedicatedCandidates = warehouses.filter((w) => w.dedicatedItemKey === null);
  // NT-POLISH-01 (P1): Eigenname vorn, Typ dahinter — zentral über utils/property-select.ts.
  const toItem = (w: typeof warehouses[number]): PropertySelectItem => ({ id: w.id, subType: w.subType, customName: w.customName, isHideout: false });
  const dedicatedName = dedicated ? formatPropertyDisplayName(toItem(dedicated)) : null;

  return `
    <h3 class="pfand-betrieb-section-title">WERKSTATT-LAGER${dedicatedName ? ` — ${esc(dedicatedName)}` : ''} (${String(o.bufferTotal)}/${String(o.bufferCapacity)} SLOTS)</h3>
    <p class="dash-meta-hint">
      ${dedicated
        ? 'Ein echtes Lagerhaus, kein separater Puffer — Bestand ist derselbe wie im Lagerhaus-Tab. Teile kommen per normalem VERSENDEN rein/raus; nur werkstatt-relevante Teile werden angenommen, alles andere lehnt der Transport ab.'
        : 'Noch kein Lager zugewiesen — erst unten ein leeres Lagerhaus zuweisen, dann Teile per VERSENDEN dorthin schicken.'}
    </p>

    <div class="werkstatt-part-group">
      <h4>GENERISCH</h4>
      ${generisch.length === 0 ? '<p class="dash-empty">Keine generischen Teile im Lager.</p>' : generisch.map(renderPartRow).join('')}
    </div>
    <div class="werkstatt-part-group">
      <h4>PREMIUM</h4>
      ${premium.length === 0 ? '<p class="dash-empty">Keine Premium-Teile im Lager.</p>' : premium.map(renderPartRow).join('')}
    </div>

    <h4 class="werkstatt-lager-heading">LAGER-ZUWEISUNG</h4>
    ${dedicated
      ? `<div class="werkstatt-part-row">
          <span>${esc(dedicatedName ?? '')}</span>
          <button class="pfand-betrieb-emp-fire" type="button" id="werkstatt-undedicate-btn" ${busy ? 'disabled' : ''}>LÖSEN</button>
        </div>`
      : `<p class="dash-meta-hint">Kein Lager der Werkstatt zugewiesen (auch im Lagerhaus-Tab per "ALS WERKSTATT-LAGER DEDIZIEREN" möglich).</p>
        ${undedicatedCandidates.length === 0 ? '' : `
          <div class="werkstatt-part-row">
            <select id="werkstatt-dedicate-select" class="pfand-betrieb-emp-select">
              ${renderPropertyOptions(undedicatedCandidates.map(toItem))}
            </select>
            <button class="btn-pfand-abgeben" type="button" id="werkstatt-dedicate-btn" ${busy ? 'disabled' : ''}>ZUWEISEN</button>
          </div>
        `}`}
  `;
}

// ─── Gesamt-Render ────────────────────────────────────────────────────────────────

function renderInner(): string {
  if (loading) return '<p class="dash-empty">Werkstatt wird geladen…</p>';
  if (!overview) return '<p class="dash-empty">Werkstatt noch nicht freigeschaltet.</p>';

  const o = overview;
  return `
    ${renderKopf(o)}
    ${renderMechanikerSektion(o)}
    ${renderKundenSektion(o)}
    ${renderPufferSektion(o)}
  `;
}

function render(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.innerHTML = renderInner();
}

function attachListeners(root: HTMLElement): void {
  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    if (target.closest('#werkstatt-hire-btn') !== null) { void handleHire(); return; }
    if (target.closest('#werkstatt-deposit-btn') !== null) { void handleDepositKasse(); return; }
    if (target.closest('#werkstatt-withdraw-btn') !== null) { void handleWithdrawKasse(); return; }
    if (target.closest('#werkstatt-undedicate-btn') !== null) { void handleUndedicate(); return; }

    const maxBtn = target.closest<HTMLButtonElement>('#werkstatt-withdraw-max-btn');
    if (maxBtn !== null && !maxBtn.disabled) { withdrawAmount = maxBtn.dataset.max ?? ''; render(); return; }

    const prioritizeBtn = target.closest<HTMLButtonElement>('[data-werkstatt-prioritize]');
    if (prioritizeBtn !== null) { void handlePrioritize(prioritizeBtn.dataset.werkstattPrioritize ?? ''); return; }

    if (target.closest('#werkstatt-dedicate-btn') !== null) {
      const select = root.querySelector<HTMLSelectElement>('#werkstatt-dedicate-select');
      if (select?.value) void handleDedicate(select.value);
      return;
    }
  });

  root.addEventListener('input', (e) => {
    const target = e.target as HTMLElement;
    if (target.id === 'werkstatt-deposit-input') depositAmount = (target as HTMLInputElement).value;
    if (target.id === 'werkstatt-withdraw-input') withdrawAmount = (target as HTMLInputElement).value;
  });
}

/** Sperrt Bedienelemente ohne vollen Re-Render (Muster PfandBetriebModal.ts::setControlsDisabled). */
function setControlsDisabled(disabled: boolean): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.querySelectorAll<HTMLButtonElement | HTMLSelectElement | HTMLInputElement>('button, select, input').forEach((el) => {
    el.disabled = disabled;
  });
}

async function withBusy(action: () => Promise<void>): Promise<void> {
  if (busy) return;
  busy = true;
  setControlsDisabled(true);
  try {
    await action();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.', 'error');
  } finally {
    busy = false;
    render();
  }
}

async function handleHire(): Promise<void> {
  await withBusy(async () => {
    await api.post('/werkstatt/betrieb/hire', {});
    void refreshBalance();
    showToast('Mechaniker eingestellt.', 'info');
  });
}

async function handlePrioritize(customerId: string): Promise<void> {
  if (!customerId) return;
  await withBusy(async () => {
    await api.post(`/werkstatt/betrieb/customer/${customerId}/prioritize`, {});
    showToast('Auftrag vorgezogen.', 'info');
  });
}

async function handleDepositKasse(): Promise<void> {
  const euros = parseFloat(depositAmount.replace(',', '.'));
  if (!Number.isFinite(euros) || euros <= 0) { showToast('Ungültiger Betrag.', 'error'); return; }
  const amountCents = Math.round(euros * 100);
  await withBusy(async () => {
    await api.post('/werkstatt/betrieb/kasse/deposit', { amountCents });
    void refreshBalance();
    depositAmount = '';
    showToast('Eingezahlt.', 'info');
  });
}

async function handleWithdrawKasse(): Promise<void> {
  const euros = parseFloat(withdrawAmount.replace(',', '.'));
  if (!Number.isFinite(euros) || euros <= 0) { showToast('Ungültiger Betrag.', 'error'); return; }
  const amountCents = Math.round(euros * 100);
  await withBusy(async () => {
    await api.post('/werkstatt/betrieb/kasse/withdraw', { amountCents });
    void refreshBalance();
    withdrawAmount = '';
    showToast('Entnommen.', 'info');
  });
}

async function handleDedicate(propertyId: string): Promise<void> {
  await withBusy(async () => {
    await api.post('/werkstatt/betrieb/lager/dedicate', { propertyId });
    showToast('Lager für die Werkstatt dediziert.', 'info');
  });
}

async function handleUndedicate(): Promise<void> {
  const dedicated = warehouses.find((w) => w.dedicatedItemKey === WERKSTATT_BUFFER_DEDICATION_KEY);
  if (!dedicated) return;
  await withBusy(async () => {
    await api.post('/werkstatt/betrieb/lager/undedicate', { propertyId: dedicated.id });
    showToast('Zuweisung gelöst.', 'info');
  });
}
